import { createContext, useState, useEffect, useCallback, useContext } from 'react'
import GaleriaDTO from '../../../dto/GaleriaDTO'
import { getDimensiones, getGalerias } from '../services/galerias.services'
import { INITIAL_ROWS_PER_PAGE } from '../components/desktop/paginadoTablaGalerias'
import { RolType } from '../../../components/Navbar/Sidebar/SidebarItems'
import { TenantsContext } from '../../../context/tenants.context'
import SnackBarUtils from '../../../utils/SnackBarUtils'

export interface PaginatorGalerias {
  handleChangePage: (e: any, newPage: number) => void
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

const defaultPaginator: PaginatorGalerias = {
  handleChangePage: () => {},
  handleChangeRowsPerPage: () => {}
}

export interface IGalerias {
  idGaleria?: any
  codGaleria?: string
  nombre: string
  direccion: string
  latitud: number
  longitud: number
  estructura: any[]
}

export interface IDimension {
  idGaleriaEstructuraDimension: number
  nombre: string
  descripcion: string
  tipoDato: string
  prefijo: string
  usarPorDefault: boolean
  valorDefault: string
  obligatorio: boolean
}

export interface newGaleria {
  nombre: string
  direccion: string
  /* latitud: number;
  longitud: number; */
}

interface GaleriasContextProps {
  galerias: IGalerias[]
  dimensiones: IDimension[]
  loading: boolean
  paginator: PaginatorGalerias
  page: number
  rowsPerPage: number
  searching: boolean
  setSearching: (searching: boolean) => void
  setLoading: (loading: boolean) => void
  setFiltro: (filtro: string) => void
  fetchData: () => Promise<void>
  fetchDimensiones: () => Promise<void>
}

// create context
export const GaleriasContext = createContext<GaleriasContextProps>({
  galerias: [],
  dimensiones: [],
  loading: false,
  paginator: defaultPaginator,
  page: 0,
  rowsPerPage: INITIAL_ROWS_PER_PAGE,
  searching: false,
  setLoading: () => {},
  setSearching: () => {},
  setFiltro: () => {},
  fetchData: async () => {},
  fetchDimensiones: async () => {}
})

// create provider
export const GaleriasProvider = ({ children }: any, props: any) => {
  const [galerias, setGalerias] = useState<IGalerias[]>([])
  const [dimensiones, setDimensiones] = useState<IDimension[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [searching, setSearching] = useState<boolean>(false)
  const [filtro, setFiltro] = useState<string>('')
  //const [actions] = useState<Actions>(defaultActions); */
  const [paginator] = useState<PaginatorGalerias>(defaultPaginator)
  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(INITIAL_ROWS_PER_PAGE)
  const { tenant } = useContext(TenantsContext)

  const fetchData = useCallback(async () => {
    const response = await getGalerias(tenant, filtro).then((res) => res.data)
    setGalerias(response.data)
  }, [filtro])

  const fetchDimensiones = useCallback(async () => {
    try {
      setLoading(true)
      const res = await getDimensiones(tenant)
      setDimensiones(res.data.data)
    } catch (e) {
      console.log(e)
      SnackBarUtils.error(e.message === 'Network Error' ? 'Error al obtener dimensiones de galerías' : e.message)
    } finally {
      setLoading(false)
    }
  }, [tenant])

  // Paginator
  paginator.handleChangePage = (e: any, newPage: number) => setPage(newPage)

  paginator.handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  useEffect(() => {
    if (!searching) return
    setLoading(true)
    Promise.all([fetchData()])
      .catch(console.log)
      .finally(() => {
        setLoading(false)
      })
  }, [filtro, searching, fetchData])

  return (
    <GaleriasContext.Provider
      value={{
        galerias,
        dimensiones,
        loading,
        setLoading,
        paginator,
        page,
        rowsPerPage,
        setFiltro,
        searching,
        setSearching,
        fetchData,
        fetchDimensiones
      }}
    >
      {children}
    </GaleriasContext.Provider>
  )
}
